import { useRect } from '@reach/rect';
import {
  Tabs as ReachTabs,
  TabList as ReachTabList,
  Tab as ReachTab,
  useTabsContext,
} from '@reach/tabs';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import React from 'react';

import Counter from './counter';

import type { TabProps, TabsProps } from '@reach/tabs';
import type { LinkOptions } from '@tanstack/react-router';

import './tabs.scss';

const HORIZONTAL_PADDING = 8;

// Reach UI 0.18 predates React 19. Its polymorphic helper drops ref from the
// inferred JSX props even though both components forward the ref at runtime.
const ReachTabsWithRef = ReachTabs as unknown as React.ForwardRefExoticComponent<
  TabsProps &
    Omit<React.ComponentPropsWithoutRef<'div'>, keyof TabsProps> &
    React.RefAttributes<HTMLDivElement>
>;
const ReachTabWithRef = ReachTab as unknown as React.ForwardRefExoticComponent<
  TabProps &
    Omit<React.ComponentPropsWithoutRef<'button'>, keyof TabProps> &
    React.RefAttributes<HTMLButtonElement>
>;

const AnimatedContext = React.createContext<React.Dispatch<
  React.SetStateAction<DOMRect | null>
> | null>(null);

interface IAnimatedTabs {
  /** Callback when a tab is chosen. */
  onChange(index: number): void;
  /** Default tab index. */
  defaultIndex: number;
  children: React.ReactNode;
}

/** Tabs with a sliding active state. */
const AnimatedTabs: React.FC<IAnimatedTabs> = ({ children, ...rest }) => {
  const [activeRect, setActiveRect] = React.useState<DOMRect | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const rect = useRect(ref);

  const top: number = (activeRect?.bottom ?? 0) - (rect?.top ?? 0);
  const width: number = (activeRect?.width ?? 0) - HORIZONTAL_PADDING * 2;
  const left: number = (activeRect?.left ?? 0) - (rect?.left ?? 0) + HORIZONTAL_PADDING;

  return (
    <AnimatedContext.Provider value={setActiveRect}>
      <ReachTabsWithRef {...rest} ref={ref}>
        <div className='tabs__bar' style={{ top }} />
        <div
          className={clsx('tabs__bar--active', {
            'tabs__bar--hidden': top <= 0,
          })}
          style={{ left, top, width }}
        />
        {children}
      </ReachTabsWithRef>
    </AnimatedContext.Provider>
  );
};

interface IAnimatedTab {
  /** Element to represent the tab. */
  as: 'button';
  /** Route to visit when the tab is chosen. */
  href?: string;
  /** Tab title text. */
  title: string;
  /** Index value of the tab. */
  index: number;
  /** Tab content. */
  children: React.ReactNode;
}

/** A single animated tab. */
const AnimatedTab: React.FC<IAnimatedTab> = ({ index, ...props }) => {
  // get the currently selected index from useTabsContext
  const { selectedIndex } = useTabsContext();
  const isSelected: boolean = selectedIndex === index;

  // measure the size of our element, only listen to rect if active
  const ref = React.useRef<HTMLButtonElement>(null);
  const rect = useRect(ref, { observe: isSelected });

  // get the style changing function from context
  const setActiveRect = React.useContext(AnimatedContext);

  // callup to set styles whenever we're active
  React.useLayoutEffect(() => {
    if (isSelected) {
      setActiveRect?.(rect);
    }
  }, [isSelected, rect, setActiveRect]);

  return <ReachTabWithRef ref={ref} {...props} />;
};

/** Structure to represent a tab. */
type Item = {
  /** Tab text. */
  text: React.ReactNode;
  /** Tab tooltip text. */
  title?: string;
  /** URL to visit when the tab is selected. */
  href?: string;
  /** Callback when the tab is selected. */
  action?: () => void;
  /** Display a counter over the tab. */
  count?: number;
  /** Unique name for this tab. */
  name: string;
} & (LinkOptions | { to?: undefined });

interface ITabs {
  /** Array of structured tab items. */
  items: Item[];
  /** Name of the active tab item. */
  activeItem: string;
}

/** Animated tabs component. */
const Tabs = ({ items, activeItem }: ITabs) => {
  const navigate = useNavigate();

  const defaultIndex = items.findIndex(({ name }) => name === activeItem);

  const onChange = (selectedIndex: number) => {
    const item = items[selectedIndex];

    if (typeof item.action === 'function') {
      item.action();
    } else if (item.to) {
      navigate({ to: item.to, params: item.params, search: item.search });
    }
  };

  const renderItem = (item: Item, idx: number) => {
    const { name, text, title, count } = item;

    return (
      <AnimatedTab key={name} as='button' title={title || ''} index={idx}>
        <div>
          {count ? (
            <span className='tab__counter'>
              <Counter count={count} />
            </span>
          ) : null}

          {text}
        </div>
      </AnimatedTab>
    );
  };

  return (
    <AnimatedTabs onChange={onChange} defaultIndex={defaultIndex}>
      <ReachTabList as='div'>{items.map((item, i) => renderItem(item, i))}</ReachTabList>
    </AnimatedTabs>
  );
};

export { type Item, Tabs as default };
